// src/pages/Dashboard.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// 📊 DASHBOARD PRINCIPAL - SISTEMA eFIT
// MULTIEMPRESA: Lee módulos desde JWT (modulos_activos) + config del tenant
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../services/api";
import { jwtDecode } from "jwt-decode";

const Dashboard = () => {
  const [usuario, setUsuario] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      const decoded = jwtDecode(token);
      setUsuario(decoded);

      const res = await api.get("/configuracion");
      setConfig(res.data);

      setLoading(false);
    } catch (error) {
      console.error("Error al cargar datos:", error);
      navigate("/");
    }
  };

  const cerrarSesion = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("usuario");
    navigate("/");
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🏢 MULTIEMPRESA: Verificar módulo activo
  // Usa modulos_activos del JWT como fuente principal,
  // con fallback a config del servidor (modulo_X: bool)
  // ═══════════════════════════════════════════════════════════════════════════
  const moduloActivo = (nombre) => {
    // SuperAdmin global tiene acceso a todo
    if (usuario?.is_superadmin && !usuario?.tenant_id) return true;

    // Config del servidor siempre es la fuente de verdad (más actualizada que el JWT)
    const mapaLegacy = {
      pos:               config?.modulo_pos,
      inventario:        config?.modulo_oficina,
      proyectos:         config?.modulo_proyectos,
      crm:               config?.modulo_crm,
      email_marketing:   config?.modulo_email_marketing,
      facturacion:       config?.modulo_facturacion,
      ncf:               config?.modulo_ncf,
      ecf:               config?.modulo_ecf,
      rentacar:          config?.modulo_rentacar,
      control_financiero:config?.modulo_control_financiero,
      almacenes:         config?.modulo_almacenes_multiple,
      transferencias:    config?.modulo_transferencias,
      prestamos:         config?.modulo_prestamos,
      contabilidad:      config?.modulo_contabilidad,
      ordenes_compra:    config?.modulo_ordenes_compra,
    };

    // Si el config ya tiene dato para este módulo, usarlo (evita depender del JWT desactualizado)
    if (mapaLegacy[nombre] !== undefined) return mapaLegacy[nombre] ?? false;

    // Fallback: JWT para módulos no mapeados en legacy
    if (usuario?.modulos_activos?.length > 0) {
      return usuario.modulos_activos.includes(nombre);
    }

    return false;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔐 DEFINICIÓN DE PERMISOS POR ROL
  // ═══════════════════════════════════════════════════════════════════════════
  const PERMISOS = {
    // POS
    pos:                  ['superadmin', 'admin', 'cajero', 'supervisor_pos'],
    clientes:             ['superadmin', 'admin', 'supervisor', 'supervisor_pos'],
    ventas_historial:     ['superadmin', 'admin', 'supervisor_pos'],
    reportes_pos:         ['superadmin', 'admin', 'supervisor_pos'],
    gestion_cajas:        ['superadmin', 'admin', 'cajero', 'supervisor_pos'],
    cajas_fisicas:        ['superadmin', 'admin', 'supervisor_pos'],
    gestion_cajas_admin:  ['superadmin', 'admin'],
    dashboard_caja:       ['superadmin', 'admin'],
    // Inventario & Oficina
    materiales:           ['superadmin', 'admin', 'supervisor', 'almacenero', 'supervisor_pos'],
    entradas:             ['superadmin', 'admin', 'supervisor', 'almacenero'],
    facturas:             ['superadmin', 'admin', 'supervisor', 'almacenero'],
    aprobaciones:         ['superadmin', 'admin', 'supervisor'],
    requisiciones:        ['superadmin', 'admin', 'supervisor', 'almacenero', 'visualizador'],
    dashboard_requisiciones: ['superadmin', 'admin', 'supervisor'],
    historial_entradas:   ['superadmin', 'admin', 'supervisor', 'almacenero'],
    salidas:              ['superadmin', 'admin', 'supervisor', 'almacenero'],
    movimientos:          ['superadmin', 'admin', 'supervisor', 'almacenero'],
    inventario_fisico:    ['superadmin', 'admin', 'supervisor', 'almacenero'],
    comparacion:          ['superadmin', 'admin', 'supervisor'],
    categorias:           ['superadmin', 'admin'],
    departamentos:        ['superadmin', 'admin'],
    usuarios:             ['superadmin', 'admin'],
    reportes:             ['superadmin', 'admin', 'supervisor'],
    salidas_lote:         ['superadmin', 'admin', 'supervisor', 'almacenero'],
    // Constructora
    almacenes:            ['superadmin', 'admin', 'supervisor', 'almacenero'],
    transferencias:       ['superadmin', 'admin', 'supervisor', 'almacenero'],
    prestamos:            ['superadmin', 'admin', 'supervisor'],
    proyectos:            ['superadmin', 'admin', 'supervisor'],
    // Facturación
    facturacion:          ['superadmin', 'admin', 'supervisor', 'contador', 'auxiliar_contable', 'supervisor_contable'],
    cotizaciones:         ['superadmin', 'admin', 'supervisor_comercial', 'vendedor'],
    configuracion_ncf:    ['superadmin', 'admin', 'contador'],
    // CRM
    crm:                  ['superadmin', 'admin', 'supervisor_comercial', 'vendedor'],
    crm_prospectos:       ['superadmin', 'admin', 'supervisor_comercial', 'vendedor'],
    crm_dashboard:        ['superadmin', 'admin', 'supervisor_comercial', 'vendedor'],
    crm_pipeline:         ['superadmin', 'admin', 'supervisor_comercial', 'vendedor'],
    crm_oportunidades:    ['superadmin', 'admin', 'supervisor_comercial', 'vendedor'],
    // Email Marketing
    email_marketing:      ['superadmin', 'admin'],
    // RentaCar
    rentacar:             ['superadmin', 'admin', 'supervisor'],
    // Contabilidad
    contabilidad:         ['superadmin', 'admin', 'contador', 'supervisor_contable'],
    dgii_reportes:        ['superadmin', 'admin', 'supervisor', 'contador', 'supervisor_contable'],
    notas_cd:             ['superadmin', 'admin', 'supervisor', 'contador', 'auxiliar_contable'],
    cuentas_pagar:        ['superadmin', 'admin', 'supervisor', 'contador', 'supervisor_contable'],
    // Módulos contables extra
    clientes:             ['superadmin', 'admin', 'supervisor', 'supervisor_pos', 'contador', 'auxiliar_contable', 'supervisor_contable'],
    nomina:               ['superadmin', 'admin', 'contador', 'supervisor_contable'],
    activos_fijos:        ['superadmin', 'admin', 'contador', 'supervisor_contable'],
    empresas_contables:   ['superadmin', 'admin', 'contador'],
    // Órdenes de Compra
    ordenes_compra:       ['superadmin', 'admin', 'supervisor', 'almacenero', 'contador', 'auxiliar_contable'],
    proveedores:          ['superadmin', 'admin', 'contador', 'supervisor_contable'],
    // Administración
    configuracion:        ['superadmin'],
    auditoria:            ['superadmin'],
    mi_suscripcion:       ['superadmin', 'admin'],
  };

  const tienePermiso = (modulo) => {
    if (!usuario?.rol) return false;
    if (usuario?.is_superadmin) return true;
    return PERMISOS[modulo]?.includes(usuario.rol) || false;
  };

  const getRolIcon = (rol) => {
    const iconos = {
      superadmin: '👑', admin: '⭐', supervisor: '🎯', cajero: '💵',
      almacenero: '📦', visualizador: '👁️',
      contador: '📒', auxiliar_contable: '🖊️', supervisor_contable: '🔍',
    };
    return iconos[rol] || '👤';
  };

  const getRolNombre = (rol) => {
    const nombres = {
      superadmin: 'Super Admin', admin: 'Administrador', supervisor: 'Supervisor',
      cajero: 'Cajero', almacenero: 'Almacenero', visualizador: 'Visualizador',
      contador: 'Contador', auxiliar_contable: 'Auxiliar Contable', supervisor_contable: 'Supervisor Contable',
    };
    return nombres[rol] || rol;
  };

  // Contar módulos activos para mostrar en el card del usuario
  const contarModulosActivos = () => {
    if (usuario?.is_superadmin && !usuario?.tenant_id) return 'Todos';
    if (usuario?.modulos_activos?.length > 0) return usuario.modulos_activos.length;
    return [config?.modulo_pos, config?.modulo_oficina, config?.modulo_proyectos,
            config?.modulo_crm, config?.modulo_email_marketing, config?.modulo_facturacion, config?.modulo_rentacar]
           .filter(Boolean).length;
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Cargando...</p>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div style={styles.container}>

      {/* ── MÓDULO POS ─────────────────────────────────────────────── */}
      {moduloActivo('pos') && tienePermiso('pos') && (
        <div style={styles.moduleSection}>
          <div style={styles.moduleSectionHeader}>
            <div style={styles.moduleSectionIcon}>🛒</div>
            <div>
              <h2 style={styles.moduleSectionTitle}>Punto de Venta</h2>
              <p style={styles.moduleSectionSubtitle}>Gestión de ventas y facturación</p>
            </div>
            <span style={styles.moduleBadgePOS}>POS</span>
          </div>
          <div style={styles.cardsGrid}>
            {tienePermiso('pos') && (
              <Link to="/pos" style={{...styles.card, ...styles.cardActive}}>
                <div style={styles.cardIcon}>🛒</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Punto de Venta</h3><p style={styles.cardDesc}>Registrar ventas al público</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {tienePermiso('ventas_historial') && (
              <Link to="/ventas" style={styles.card}>
                <div style={styles.cardIcon}>📊</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Ventas</h3><p style={styles.cardDesc}>Historial y reportes</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {tienePermiso('reportes_pos') && (
              <Link to="/reportes-pos" style={styles.card}>
                <div style={styles.cardIcon}>📈</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Reportes POS</h3><p style={styles.cardDesc}>Análisis de ventas</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {tienePermiso('gestion_cajas') && (
              <Link to="/gestion-cajas" style={styles.card}>
                <div style={styles.cardIcon}>💰</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Gestión de Cajas</h3><p style={styles.cardDesc}>Apertura y cierre de caja</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {tienePermiso('gestion_cajas_admin') && (
              <Link to="/gestion-cajas-admin" style={styles.card}>
                <div style={styles.cardIcon}>💼</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Panel de Cajas</h3><p style={styles.cardDesc}>Supervisar todas las cajas</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {tienePermiso('dashboard_caja') && (
              <Link to="/dashboard-caja" style={styles.card}>
                <div style={styles.cardIcon}>📺</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Dashboard de Caja</h3><p style={styles.cardDesc}>Ventas en tiempo real</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {tienePermiso('cajas_fisicas') && (
              <Link to="/cajas-fisicas" style={styles.card}>
                <div style={styles.cardIcon}>🏪</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Cajas Físicas</h3><p style={styles.cardDesc}>Administrar cajas del negocio</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* ── MÓDULO INVENTARIO & OFICINA ────────────────────────────── */}
      {moduloActivo('inventario') && (tienePermiso('materiales') || tienePermiso('requisiciones')) && (
        <div style={styles.moduleSection}>
          <div style={styles.moduleSectionHeader}>
            <div style={styles.moduleSectionIcon}>📦</div>
            <div>
              <h2 style={styles.moduleSectionTitle}>Inventario & Oficina</h2>
              <p style={styles.moduleSectionSubtitle}>Gestión de materiales y requisiciones</p>
            </div>
            <span style={styles.moduleBadgeInventario}>INVENTARIO</span>
          </div>
          <div style={styles.cardsGrid}>
            {tienePermiso('materiales') && (<Link to="/materiales" style={styles.card}><div style={styles.cardIcon}>📦</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Materiales</h3><p style={styles.cardDesc}>Gestión de inventario y stock</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('entradas') && (<Link to="/entradas" style={styles.card}><div style={styles.cardIcon}>📥</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Entradas</h3><p style={styles.cardDesc}>Recepciones y facturas</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('facturas') && (<Link to="/entrada-factura" style={styles.card}><div style={styles.cardIcon}>📥</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Entrada Lote</h3><p style={styles.cardDesc}>Registro de entradas por lote</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('aprobaciones') && (<Link to="/entradas-pendientes" style={styles.card}><div style={styles.cardIcon}>✅</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Aprobaciones</h3><p style={styles.cardDesc}>Aprobar entradas</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('requisiciones') && (<Link to="/requisiciones" style={styles.card}><div style={styles.cardIcon}>📝</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Requisiciones</h3><p style={styles.cardDesc}>Solicitudes de materiales</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('dashboard_requisiciones') && (<Link to="/dashboard-requisiciones" style={styles.card}><div style={styles.cardIcon}>📊</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Dashboard Requisiciones</h3><p style={styles.cardDesc}>Resumen de requisiciones</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('historial_entradas') && (<Link to="/entradas-historial" style={styles.card}><div style={styles.cardIcon}>📜</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Historial Entradas</h3><p style={styles.cardDesc}>Registro de entradas</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('salidas') && (<Link to="/salidas" style={styles.card}><div style={styles.cardIcon}>📤</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Salidas</h3><p style={styles.cardDesc}>Salidas y requisiciones</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('salidas_lote') && (<Link to="/salidas/lote" style={styles.card}><div style={styles.cardIcon}>📦</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Salida por Lote</h3><p style={styles.cardDesc}>Registrar múltiples salidas</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('movimientos') && (<Link to="/movimientos" style={styles.card}><div style={styles.cardIcon}>🔄</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Movimientos</h3><p style={styles.cardDesc}>Historial de movimientos</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('inventario_fisico') && (<Link to="/inventario/fisico" style={styles.card}><div style={styles.cardIcon}>📋</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Inventario Físico</h3><p style={styles.cardDesc}>Conteo físico</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('comparacion') && (<Link to="/inventario/comparacion" style={styles.card}><div style={styles.cardIcon}>⚖️</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Comparación</h3><p style={styles.cardDesc}>Comparar inventarios</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('categorias') && (<Link to="/categorias" style={styles.card}><div style={styles.cardIcon}>📂</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Categorías</h3><p style={styles.cardDesc}>Gestión de categorías</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('reportes') && (<Link to="/reportes" style={styles.card}><div style={styles.cardIcon}>📊</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Reportes</h3><p style={styles.cardDesc}>Análisis de inventario</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {/* Almacenes — parte del inventario */}
            {moduloActivo('almacenes') && tienePermiso('almacenes') && (<Link to="/almacenes" style={styles.card}><div style={styles.cardIcon}>🏢</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Almacenes</h3><p style={styles.cardDesc}>Gestión de almacenes múltiples</p></div><span style={styles.cardArrow}>→</span></Link>)}
          </div>
        </div>
      )}

      {/* ── MÓDULO CONSTRUCTORA / PROYECTOS ────────────────────────── */}
      {moduloActivo('proyectos') && (tienePermiso('proyectos') || tienePermiso('transferencias') || tienePermiso('prestamos')) && (
        <div style={styles.moduleSection}>
          <div style={styles.moduleSectionHeader}>
            <div style={styles.moduleSectionIcon}>🏗️</div>
            <div>
              <h2 style={styles.moduleSectionTitle}>Constructora & Proyectos</h2>
              <p style={styles.moduleSectionSubtitle}>Gestión de obras, transferencias y préstamos</p>
            </div>
            <span style={styles.moduleBadgeConstructora}>CONSTRUCTORA</span>
          </div>
          <div style={styles.cardsGrid}>
            {tienePermiso('proyectos') && (<Link to="/proyectos" style={styles.card}><div style={styles.cardIcon}>🏗️</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Proyectos</h3><p style={styles.cardDesc}>Gestión de obras y asignaciones</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {moduloActivo('transferencias') && tienePermiso('transferencias') && (<Link to="/transferencias" style={styles.card}><div style={styles.cardIcon}>🔄</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Transferencias</h3><p style={styles.cardDesc}>Movimientos entre almacenes</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {moduloActivo('prestamos') && tienePermiso('prestamos') && (<Link to="/prestamos" style={styles.card}><div style={styles.cardIcon}>🤝</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Préstamos</h3><p style={styles.cardDesc}>Préstamos entre proyectos</p></div><span style={styles.cardArrow}>→</span></Link>)}
            <Link to="/partidas-estandar" style={styles.card}><div style={styles.cardIcon}>📋</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Partidas Estándar</h3><p style={styles.cardDesc}>Catálogo de partidas para presupuestos</p></div><span style={styles.cardArrow}>→</span></Link>
            <Link to="/certificaciones" style={styles.card}><div style={styles.cardIcon}>📜</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Certificaciones</h3><p style={styles.cardDesc}>Certificar avances de obra</p></div><span style={styles.cardArrow}>→</span></Link>
          </div>
        </div>
      )}

      {/* ── MÓDULO CRM ─────────────────────────────────────────────── */}
      {moduloActivo('crm') && tienePermiso('crm') && (
        <div style={styles.moduleSection}>
          <div style={styles.moduleSectionHeader}>
            <div style={styles.moduleSectionIcon}>📞</div>
            <div>
              <h2 style={styles.moduleSectionTitle}>CRM Comercial</h2>
              <p style={styles.moduleSectionSubtitle}>Gestión de prospectos y ventas</p>
            </div>
            <span style={styles.moduleBadgeCRM}>CRM</span>
          </div>
          <div style={styles.cardsGrid}>
            {tienePermiso('crm_dashboard') && (<Link to="/crm/dashboard" style={styles.card}><div style={styles.cardIcon}>📊</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Dashboard</h3><p style={styles.cardDesc}>Métricas y análisis comercial</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('crm_prospectos') && (<Link to="/crm/prospectos" style={styles.card}><div style={styles.cardIcon}>📋</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Prospectos</h3><p style={styles.cardDesc}>Leads y contactos potenciales</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('crm_pipeline') && (<Link to="/crm/pipeline" style={styles.card}><div style={styles.cardIcon}>🎯</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Pipeline</h3><p style={styles.cardDesc}>Embudo de ventas visual</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('crm_oportunidades') && (<Link to="/crm/oportunidades" style={styles.card}><div style={styles.cardIcon}>💼</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Oportunidades</h3><p style={styles.cardDesc}>Seguimiento de ventas</p></div><span style={styles.cardArrow}>→</span></Link>)}
          </div>
        </div>
      )}

      {/* ── MÓDULO EMAIL MARKETING ─────────────────────────────────── */}
      {moduloActivo('email_marketing') && tienePermiso('email_marketing') && (
        <div style={styles.moduleSection}>
          <div style={styles.moduleSectionHeader}>
            <div style={styles.moduleSectionIcon}>📧</div>
            <div>
              <h2 style={styles.moduleSectionTitle}>Email Marketing</h2>
              <p style={styles.moduleSectionSubtitle}>Campañas, plantillas y grupos de contactos</p>
            </div>
            <span style={{...styles.moduleBadgeInventario, background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)'}}>EMAIL</span>
          </div>
          <div style={styles.cardsGrid}>
            <Link to="/email" style={styles.card}>
              <div style={styles.cardIcon}>📊</div>
              <div style={styles.cardContent}><h3 style={styles.cardTitle}>Dashboard</h3><p style={styles.cardDesc}>Estadísticas y resumen de campañas</p></div>
              <span style={styles.cardArrow}>→</span>
            </Link>
            <Link to="/email/campanas" style={styles.card}>
              <div style={styles.cardIcon}>📨</div>
              <div style={styles.cardContent}><h3 style={styles.cardTitle}>Campañas</h3><p style={styles.cardDesc}>Crear y gestionar campañas de email</p></div>
              <span style={styles.cardArrow}>→</span>
            </Link>
            <Link to="/email/plantillas" style={styles.card}>
              <div style={styles.cardIcon}>📝</div>
              <div style={styles.cardContent}><h3 style={styles.cardTitle}>Plantillas</h3><p style={styles.cardDesc}>Diseños reutilizables para correos</p></div>
              <span style={styles.cardArrow}>→</span>
            </Link>
            <Link to="/email/grupos" style={styles.card}>
              <div style={styles.cardIcon}>👥</div>
              <div style={styles.cardContent}><h3 style={styles.cardTitle}>Grupos</h3><p style={styles.cardDesc}>Listas y segmentos de contactos</p></div>
              <span style={styles.cardArrow}>→</span>
            </Link>
          </div>
        </div>
      )}

      {/* ── MÓDULO RENTACAR ────────────────────────────────────────── */}
      {moduloActivo('rentacar') && tienePermiso('rentacar') && (
        <div style={styles.moduleSection}>
          <div style={styles.moduleSectionHeader}>
            <div style={styles.moduleSectionIcon}>🚗</div>
            <div>
              <h2 style={styles.moduleSectionTitle}>RentaCar</h2>
              <p style={styles.moduleSectionSubtitle}>Sistema de renta de vehículos</p>
            </div>
            <span style={{...styles.moduleBadgeInventario, background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)'}}>RENTACAR</span>
          </div>
          <div style={styles.cardsGrid}>
            <Link to="/rencar" style={styles.card}><div style={styles.cardIcon}>📊</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Dashboard</h3><p style={styles.cardDesc}>Métricas y estadísticas</p></div><span style={styles.cardArrow}>→</span></Link>
            <Link to="/rencar/vehiculos" style={styles.card}><div style={styles.cardIcon}>🚙</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Vehículos</h3><p style={styles.cardDesc}>Gestión de flota</p></div><span style={styles.cardArrow}>→</span></Link>
            <Link to="/rencar/rentas/crear" style={{...styles.card, borderColor: '#16a34a'}}><div style={styles.cardIcon}>➕</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Nueva Renta</h3><p style={styles.cardDesc}>Crear renta de vehículo</p></div><span style={styles.cardArrow}>→</span></Link>
            <Link to="/rencar/rentas-abiertas" style={styles.card}><div style={styles.cardIcon}>📋</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Rentas Abiertas</h3><p style={styles.cardDesc}>Vehículos en renta</p></div><span style={styles.cardArrow}>→</span></Link>
            <Link to="/rencar/reportes" style={styles.card}><div style={styles.cardIcon}>📈</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Reportes</h3><p style={styles.cardDesc}>Ingresos y estadísticas</p></div><span style={styles.cardArrow}>→</span></Link>
          </div>
        </div>
      )}

      {/* ── MÓDULO CLIENTES ────────────────────────────────────────── */}
      {moduloActivo('clientes') && tienePermiso('clientes') && (
        <div style={styles.moduleSection}>
          <div style={styles.moduleSectionHeader}>
            <div style={styles.moduleSectionIcon}>👥</div>
            <div>
              <h2 style={styles.moduleSectionTitle}>Clientes</h2>
              <p style={styles.moduleSectionSubtitle}>Gestión de clientes del negocio</p>
            </div>
            <span style={{...styles.moduleBadgeInventario, background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)'}}>CLIENTES</span>
          </div>
          <div style={styles.cardsGrid}>
            <Link to="/clientes" style={styles.card}>
              <div style={styles.cardIcon}>👥</div>
              <div style={styles.cardContent}><h3 style={styles.cardTitle}>Clientes</h3><p style={styles.cardDesc}>Gestión de clientes</p></div>
              <span style={styles.cardArrow}>→</span>
            </Link>
          </div>
        </div>
      )}

      {/* ── MÓDULO FACTURACIÓN ─────────────────────────────────────── */}
      {moduloActivo('facturacion') && tienePermiso('facturacion') && (
        <div style={styles.moduleSection}>
          <div style={styles.moduleSectionHeader}>
            <div style={styles.moduleSectionIcon}>🧾</div>
            <div>
              <h2 style={styles.moduleSectionTitle}>Facturación</h2>
              <p style={styles.moduleSectionSubtitle}>Facturación y cuentas por cobrar</p>
            </div>
            <span style={{...styles.moduleBadgeInventario, background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)'}}>FACTURACIÓN</span>
          </div>
          <div style={styles.cardsGrid}>
            <Link to="/facturas" style={styles.card}><div style={styles.cardIcon}>📄</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Facturas</h3><p style={styles.cardDesc}>Gestión de facturación</p></div><span style={styles.cardArrow}>→</span></Link>
            <Link to="/facturas/dashboard" style={styles.card}><div style={styles.cardIcon}>💰</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Cuentas por Cobrar</h3><p style={styles.cardDesc}>Dashboard ejecutivo de cobranza</p></div><span style={styles.cardArrow}>→</span></Link>
            {moduloActivo('cotizaciones') && tienePermiso('cotizaciones') && (<Link to="/cotizaciones" style={styles.card}><div style={styles.cardIcon}>📄</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Cotizaciones</h3><p style={styles.cardDesc}>Crear y gestionar cotizaciones</p></div><span style={styles.cardArrow}>→</span></Link>)}
          </div>
        </div>
      )}

      {/* ── MÓDULO ÓRDENES DE COMPRA ───────────────────────────────── */}
      {moduloActivo('ordenes_compra') && tienePermiso('ordenes_compra') && (
        <div style={styles.moduleSection}>
          <div style={styles.moduleSectionHeader}>
            <div style={styles.moduleSectionIcon}>🛒</div>
            <div>
              <h2 style={styles.moduleSectionTitle}>Órdenes de Compra</h2>
              <p style={styles.moduleSectionSubtitle}>Proveedores, órdenes y recepción de mercancía</p>
            </div>
            <span style={{...styles.moduleBadgeInventario, background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)'}}>COMPRAS</span>
          </div>
          <div style={styles.cardsGrid}>
            {tienePermiso('ordenes_compra') && (
              <Link to="/ordenes-compra" style={styles.card}>
                <div style={styles.cardIcon}>📋</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Órdenes de Compra</h3><p style={styles.cardDesc}>Crear y gestionar órdenes a proveedores</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {moduloActivo('proveedores') && tienePermiso('proveedores') && (
              <Link to="/proveedores" style={styles.card}>
                <div style={styles.cardIcon}>🏭</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Proveedores</h3><p style={styles.cardDesc}>Gestión del catálogo de proveedores</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* ── MÓDULO CONTABILIDAD ────────────────────────────────────── */}
      {moduloActivo('contabilidad') && tienePermiso('contabilidad') && (
        <div style={styles.moduleSection}>
          <div style={styles.moduleSectionHeader}>
            <div style={styles.moduleSectionIcon}>📒</div>
            <div>
              <h2 style={styles.moduleSectionTitle}>Contabilidad</h2>
              <p style={styles.moduleSectionSubtitle}>Doble entrada, DGII, cuentas por pagar y notas fiscales</p>
            </div>
            <span style={{...styles.moduleBadgeInventario, background: 'linear-gradient(135deg, #1e3a8a 0%, #0066cc 100%)'}}>CONTABILIDAD</span>
          </div>
          <div style={styles.cardsGrid}>
            {tienePermiso('contabilidad') && (
              <Link to="/contabilidad" style={{...styles.card, borderColor: '#1e3a8a'}}>
                <div style={styles.cardIcon}>📒</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Plan de Cuentas</h3><p style={styles.cardDesc}>Cuentas contables y asientos de doble entrada</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {moduloActivo('reportes_dgii') && tienePermiso('dgii_reportes') && (
              <Link to="/dgii/reportes" style={styles.card}>
                <div style={styles.cardIcon}>🏛</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Reportes DGII</h3><p style={styles.cardDesc}>Formularios 606, 607 y 608</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {tienePermiso('notas_cd') && (
              <Link to="/notas" style={styles.card}>
                <div style={styles.cardIcon}>📝</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Notas de Crédito/Débito</h3><p style={styles.cardDesc}>Emisión y gestión de notas fiscales</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {moduloActivo('cxp') && tienePermiso('cuentas_pagar') && (
              <Link to="/cuentas-pagar" style={styles.card}>
                <div style={styles.cardIcon}>💳</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Cuentas por Pagar</h3><p style={styles.cardDesc}>Gestión de pagos a proveedores y aging</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {moduloActivo('banco') && (tienePermiso('contabilidad') || tienePermiso('nomina')) && (
              <Link to="/bancos" style={styles.card}>
                <div style={styles.cardIcon}>🏦</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Bancos & Tesorería</h3><p style={styles.cardDesc}>Cuentas bancarias, movimientos y conciliación</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {tienePermiso('contabilidad') && (
              <Link to="/empleados" style={styles.card}>
                <div style={styles.cardIcon}>👷</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Empleados</h3><p style={styles.cardDesc}>Directorio global de empleados y cargos</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {moduloActivo('nomina') && tienePermiso('nomina') && (
              <Link to="/nomina" style={styles.card}>
                <div style={styles.cardIcon}>💰</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Nómina Universal</h3><p style={styles.cardDesc}>Procesamiento con TSS e ISR automáticos</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {moduloActivo('activos_fijos') && tienePermiso('activos_fijos') && (
              <Link to="/activos-fijos" style={styles.card}>
                <div style={styles.cardIcon}>🏗️</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Activos Fijos</h3><p style={styles.cardDesc}>Registro y depreciación de activos fijos</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {tienePermiso('empresas_contables') && (
              <Link to="/empresas-contables" style={styles.card}>
                <div style={styles.cardIcon}>🏢</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Empresas Contables</h3><p style={styles.cardDesc}>Gestiona los clientes del despacho contable</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            <Link to="/manual" style={styles.card}>
              <div style={styles.cardIcon}>📖</div>
              <div style={styles.cardContent}><h3 style={styles.cardTitle}>Manual de Usuario</h3><p style={styles.cardDesc}>Guía paso a paso de todos los módulos contables</p></div>
              <span style={styles.cardArrow}>→</span>
            </Link>
          </div>
        </div>
      )}

      {/* ── MÓDULO NCF / e-CF ──────────────────────────────────────── */}
      {(moduloActivo('ncf') || moduloActivo('ecf')) && (
        <div style={styles.moduleSection}>
          <div style={styles.moduleSectionHeader}>
            <div style={styles.moduleSectionIcon}>📋</div>
            <div>
              <h2 style={styles.moduleSectionTitle}>Comprobantes Fiscales</h2>
              <p style={styles.moduleSectionSubtitle}>Secuencias NCF y facturación electrónica DGII</p>
            </div>
            <span style={styles.moduleBadgeConfig}>NCF</span>
          </div>
          <div style={styles.cardsGrid}>
            {moduloActivo('ncf') && (
              <Link to="/configuracion-ncf" style={styles.card}><div style={styles.cardIcon}>📋</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Gestión NCF</h3><p style={styles.cardDesc}>Secuencias y configuración fiscal</p></div><span style={styles.cardArrow}>→</span></Link>
            )}
            {moduloActivo('ecf') && tienePermiso('configuracion_ncf') && (
              <Link to="/configuracion/ecf" style={styles.card}>
                <div style={styles.cardIcon}>📡</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>e-CF Electrónico</h3><p style={styles.cardDesc}>Envío de e-CF a DGII vía Alanube</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* ── ADMINISTRACIÓN ─────────────────────────────────────────── */}
      {(tienePermiso('configuracion') || tienePermiso('usuarios') || tienePermiso('departamentos')) && (
        <div style={styles.moduleSection}>
          <div style={styles.moduleSectionHeader}>
            <div style={styles.moduleSectionIcon}>⚙️</div>
            <div>
              <h2 style={styles.moduleSectionTitle}>Administración</h2>
              <p style={styles.moduleSectionSubtitle}>Configuración del sistema</p>
            </div>
            <span style={styles.moduleBadgeConfig}>ADMIN</span>
          </div>
          <div style={styles.cardsGrid}>
            {tienePermiso('usuarios') && (<Link to="/usuarios" style={styles.card}><div style={styles.cardIcon}>👥</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Usuarios</h3><p style={styles.cardDesc}>Gestión de usuarios</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('departamentos') && (<Link to="/departamentos" style={styles.card}><div style={styles.cardIcon}>🏢</div><div style={styles.cardContent}><h3 style={styles.cardTitle}>Departamentos</h3><p style={styles.cardDesc}>Gestión de departamentos</p></div><span style={styles.cardArrow}>→</span></Link>)}
            {tienePermiso('configuracion') && (
              <Link to="/configuracion" style={{...styles.card, borderColor: '#f59e0b'}}>
                <div style={styles.cardIcon}>⚙️</div>
                <div style={styles.cardContent}><h3 style={styles.cardTitle}>Configuración</h3><p style={styles.cardDesc}>Módulos y ajustes del sistema</p></div>
                <span style={styles.cardArrow}>→</span>
              </Link>
            )}
            {tienePermiso('mi_suscripcion') && (
              <Link to="/mi-suscripcion" style={{...styles.card, borderColor: '#8b5cf6', background: 'linear-gradient(135deg, #6366f108, #8b5cf608)'}}>
                <div style={{...styles.cardIcon, color: '#7c3aed'}}>💳</div>
                <div style={styles.cardContent}><h3 style={{...styles.cardTitle, color: '#5b21b6'}}>Mi Suscripción</h3><p style={styles.cardDesc}>Plan, tarjeta y facturas</p></div>
                <span style={{...styles.cardArrow, color: '#7c3aed'}}>→</span>
              </Link>
            )}
            {tienePermiso('auditoria') && (
              <Link to="/admin/auditoria" style={{...styles.card, borderColor: '#ef4444', background: '#fef2f2'}}>
                <div style={{...styles.cardIcon, color: '#ef4444'}}>🛡️</div>
                <div style={styles.cardContent}><h3 style={{...styles.cardTitle, color: '#991b1b'}}>Auditoría</h3><p style={styles.cardDesc}>Rastreo de cambios y logs</p></div>
                <span style={{...styles.cardArrow, color: '#ef4444'}}>→</span>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* ── 👑 SUPER ADMIN PANEL — Solo visible si is_superadmin ───── */}
      {usuario?.is_superadmin && (
        <div style={{...styles.moduleSection, border: '2px solid #f59e0b', background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)'}}>
          <div style={styles.moduleSectionHeader}>
            <div style={{...styles.moduleSectionIcon, background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '2px solid #f59e0b'}}>👑</div>
            <div>
              <h2 style={{...styles.moduleSectionTitle, color: '#92400e'}}>Super Admin</h2>
              <p style={styles.moduleSectionSubtitle}>Gestión global de empresas y planes</p>
            </div>
            <span style={{...styles.moduleBadgeConfig, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>SUPERADMIN</span>
          </div>
          <div style={styles.cardsGrid}>
            <Link to="/superadmin" style={{...styles.card, borderColor: '#f59e0b'}}>
              <div style={styles.cardIcon}>🏢</div>
              <div style={styles.cardContent}><h3 style={styles.cardTitle}>Empresas</h3><p style={styles.cardDesc}>Gestionar tenants y empresas</p></div>
              <span style={styles.cardArrow}>→</span>
            </Link>
            <Link to="/superadmin" style={{...styles.card, borderColor: '#f59e0b'}}>
              <div style={styles.cardIcon}>📦</div>
              <div style={styles.cardContent}><h3 style={styles.cardTitle}>Planes</h3><p style={styles.cardDesc}>Configurar planes y precios</p></div>
              <span style={styles.cardArrow}>→</span>
            </Link>
            <Link to="/superadmin" style={{...styles.card, borderColor: '#f59e0b'}}>
              <div style={styles.cardIcon}>📊</div>
              <div style={styles.cardContent}><h3 style={styles.cardTitle}>Audit Log</h3><p style={styles.cardDesc}>Eventos globales del sistema</p></div>
              <span style={styles.cardArrow}>→</span>
            </Link>
          </div>
        </div>
      )}

      {/* ── INFO DEL USUARIO ───────────────────────────────────────── */}
      <div style={styles.userInfoCard}>
        <div style={styles.userInfoHeader}>
          <div style={styles.userAvatar}>{getRolIcon(usuario?.rol)}</div>
          <div>
            <h3 style={styles.userInfoName}>{usuario?.nombre || usuario?.email}</h3>
            <p style={styles.userInfoRole}>{getRolNombre(usuario?.rol)}</p>
            {usuario?.tenant_nombre && (
              <p style={{...styles.userInfoRole, opacity: 0.7, fontSize: '12px'}}>
                🏢 {usuario.tenant_nombre}
              </p>
            )}
          </div>
          <button onClick={cerrarSesion} style={styles.btnCerrarSesion}>
            Cerrar Sesión
          </button>
        </div>
        <div style={styles.userInfoStats}>
          <div style={styles.userInfoStat}>
            <span style={styles.statLabel}>Módulos activos:</span>
            <span style={styles.statValue}>{contarModulosActivos()}</span>
          </div>
          <div style={styles.userInfoStat}>
            <span style={styles.statLabel}>Tu acceso:</span>
            <span style={styles.statValue}>
              {usuario?.is_superadmin ? '👑 Global' :
               usuario?.rol === 'admin' ? 'Administrativo' :
               usuario?.rol === 'supervisor' ? 'Supervisión' :
               usuario?.rol === 'cajero' ? 'POS' :
               usuario?.rol === 'almacenero' ? 'Inventario' :
               usuario?.rol === 'contador' ? 'Contabilidad Completa' :
               usuario?.rol === 'auxiliar_contable' ? 'Contabilidad Básica' :
               usuario?.rol === 'supervisor_contable' ? 'Solo Lectura Contable' : 'Limitado'}
            </span>
          </div>
          {usuario?.tenant_id && (
            <div style={styles.userInfoStat}>
              <span style={styles.statLabel}>Empresa ID:</span>
              <span style={styles.statValue}>#{usuario.tenant_id}</span>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS
// ═══════════════════════════════════════════════════════════════════════════════
const styles = {
  container: { minHeight: "calc(100vh - 70px)", background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)", padding: "24px 32px", fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif" },
  loadingContainer: { minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%)" },
  spinner: { width: "40px", height: "40px", border: "4px solid #e2e8f0", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  loadingText: { marginTop: "16px", color: "#64748b", fontSize: "16px" },
  moduleSection: { background: "white", borderRadius: "20px", padding: "24px", marginBottom: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", border: "1px solid rgba(59, 130, 246, 0.1)" },
  moduleSectionHeader: { display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px", paddingBottom: "16px", borderBottom: "2px solid #f1f5f9" },
  moduleSectionIcon: { fontSize: "40px", background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)", padding: "14px", borderRadius: "16px", border: "2px solid #93c5fd" },
  moduleSectionTitle: { fontSize: "22px", fontWeight: "700", color: "#1e293b", margin: 0 },
  moduleSectionSubtitle: { fontSize: "14px", color: "#64748b", margin: "4px 0 0 0" },
  moduleBadgePOS: { marginLeft: "auto", padding: "8px 16px", background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "white", borderRadius: "20px", fontSize: "12px", fontWeight: "700", letterSpacing: "1px" },
  moduleBadgeInventario: { marginLeft: "auto", padding: "8px 16px", background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", color: "white", borderRadius: "20px", fontSize: "12px", fontWeight: "700", letterSpacing: "1px" },
  moduleBadgeConfig: { marginLeft: "auto", padding: "8px 16px", background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", color: "white", borderRadius: "20px", fontSize: "12px", fontWeight: "700", letterSpacing: "1px" },
  moduleBadgeConstructora: { marginLeft: "auto", padding: "8px 16px", background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", color: "white", borderRadius: "20px", fontSize: "12px", fontWeight: "700", letterSpacing: "1px" },
  moduleBadgeCRM: { marginLeft: "auto", padding: "8px 16px", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white", borderRadius: "20px", fontSize: "12px", fontWeight: "700", letterSpacing: "1px" },
  cardsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "16px" },
  card: { display: "flex", alignItems: "center", gap: "16px", padding: "20px", background: "white", border: "2px solid #e2e8f0", borderRadius: "14px", textDecoration: "none", color: "#1e293b", transition: "all 0.2s ease", cursor: "pointer" },
  cardActive: { borderColor: "#10b981", background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)" },
  cardIcon: { fontSize: "32px", background: "#f8fafc", padding: "12px", borderRadius: "12px", flexShrink: 0 },
  cardContent: { flex: 1 },
  cardTitle: { fontSize: "16px", fontWeight: "700", color: "#1e293b", margin: 0 },
  cardDesc: { fontSize: "13px", color: "#64748b", margin: "4px 0 0 0" },
  cardArrow: { fontSize: "20px", color: "#10b981", fontWeight: "700" },
  userInfoCard: { background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", borderRadius: "20px", padding: "24px", color: "white", marginTop: "24px" },
  userInfoHeader: { display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px", paddingBottom: "16px", borderBottom: "1px solid rgba(255,255,255,0.2)" },
  userAvatar: { width: "60px", height: "60px", background: "rgba(255,255,255,0.2)", borderRadius: "16px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px" },
  userInfoName: { fontSize: "20px", fontWeight: "700", margin: 0 },
  userInfoRole: { fontSize: "14px", opacity: 0.85, margin: "4px 0 0 0" },
  userInfoStats: { display: "flex", gap: "24px", flexWrap: "wrap" },
  userInfoStat: { display: "flex", flexDirection: "column", gap: "4px" },
  statLabel: { fontSize: "12px", opacity: 0.75 },
  statValue: { fontSize: "16px", fontWeight: "700" },
  btnCerrarSesion: { marginLeft: "auto", padding: "10px 20px", background: "rgba(255,255,255,0.15)", color: "white", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "10px", cursor: "pointer", fontSize: "14px", fontWeight: "600" },
};

export default Dashboard;